import { FC, useContext, useEffect, useState } from 'react';
import styled from 'styled-components';
import { formatDate } from '../common/Date';
import { AppContext } from '../contexts/AppContext';
import { fetchMinneContent } from '../services/Api';
import BlockContainer from './BlockContainer';
import BlockError from './BlockError';
import FadeImage from './FadeImage';
import Pagination from './Pagination';
import MinneModal from './MinneModal';
import Avatar from './Avatar';
import CardGrid, { getCardColumns, onCardKeyDown } from './CardGrid';
import useBlockPaging from '../hooks/useBlockPaging';
import { getImageUrl, preloadImages } from '../common/Media';
import { DM_DMS_IMAGE_URL, MEDIA_PREFIX } from '../Settings';
import { getMinneAnswer, getMinneText, MinneCountry, parseMinneDate } from '../common/Minne';
import { u } from '../common/Units';
import QuoteIcon from '../assets/icon_quote.svg?react';

interface BlockMinneProps {
  topicId: any,
  domain?: string,
  sortOrder?: string,
  resultCount?: number
}

const Card = styled.li`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background-color: ${p => p.theme.blockColor};
  cursor: pointer;
  transition: transform 0.15s;

  &:active {
    transform: scale(0.98);
  }

  & > img {
    display: block;
    width: 100%;
    height: ${u(130)};
    object-fit: cover;
    background-color: rgba(255, 255, 255, 0.08);
  }
`;

const CardBody = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: ${u(18)} ${u(20)} ${u(18)};
`;

const Quote = styled(QuoteIcon)`
  width: ${u(20)};
  height: auto;
  margin-bottom: ${u(8)};

  & path {
    fill: ${p => p.theme.accentColor};
  }
`;

const CardTitle = styled.h3`
  margin: 0 0 ${u(6)};
  font-size: 0.95em;
`;

/** The first answer, up to three lines */
const Excerpt = styled.p`
  margin: 0 0 ${u(14)};
  font-size: 0.85em;
  line-height: 1.45;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
  overflow: hidden;
`;

/** At the bottom of the card, also when the excerpt is short */
const Byline = styled.div`
  margin-top: auto;
  display: flex;
  align-items: center;
  font-size: 0.75em;
  line-height: 1.3;
  gap: ${u(12)};

  & div > span {
    display: block;
    opacity: 0.7;
  }
`;

/** URL of a card's image */
const getCardImageUrl = (dmsId: string): string => `${MEDIA_PREFIX}${getImageUrl(DM_DMS_IMAGE_URL.replace("{id}", dmsId), { dimension: "600x600" })}`;

/**
 * Responses to a Minne topic as cards with the start of the first answer. A tap opens the whole response in a popup
 * @returns {JSX.Element} Component template
 */
export const BlockMinne: FC<BlockMinneProps> = ({topicId, domain, sortOrder, resultCount}) => {
  const { previewIsActive, currentLanguage } = useContext(AppContext);
  const [minneData, setMinneData] = useState<any>({});
  const [country, setCountry] = useState<MinneCountry>("no");
  const [hasError, setHasError] = useState<boolean>(false);
  const [selected, setSelected] = useState<any>(null);
  const pageSize = resultCount || 12;
  const isValidTopic = typeof topicId === "string" && topicId.length === 36;
  const { currentPage, setCurrentPage, listRef, listStyle, idleResetCount } = useBlockPaging(`${topicId}|${domain}|${sortOrder}|${pageSize}`);

  // Fetch a page of responses. The domain decides the country; older blocks without it try Norway, then Sweden
  useEffect(() => {
    if (!isValidTopic) { return; }
    let isActive = true;
    const query = `responses/?topic=${topicId}&offset=${pageSize * (currentPage - 1)}&limit=${pageSize}&order=${sortOrder || ""}`;
    const countries: MinneCountry[] = domain ? [domain.includes(".se") ? "sv" : "no"] : ["no", "sv"];

    (async () => {
      for (const code of countries) {
        const data = await fetchMinneContent(query, code).catch(() => null);
        if (!isActive) { return; }
        if (data?.items) {
          // The current page stays until the next page's images are ready, so the cards change in one go
          await preloadImages(data.items.filter((item: any) => item?.image_dms_id).map((item: any) => getCardImageUrl(item.image_dms_id)));
          if (!isActive) { return; }
          setMinneData(data);
          setCountry(code);
          setHasError(false);
          return;
        }
      }
      setHasError(true);
    })();

    return () => { isActive = false; };
  }, [topicId, isValidTopic, domain, sortOrder, pageSize, currentPage]);

  // Close the popup when idle mode resets the kiosk
  useEffect(() => { setSelected(null); }, [idleResetCount]);

  if (!topicId) { return null; }

  if (previewIsActive && (!isValidTopic || hasError)) {
    return <BlockError text={`Kunne ikke hente data fra Minne-APIet for topicId '${topicId}'.`}></BlockError>;
  }

  return (
    <BlockContainer>
      {minneData?.items && (
        <CardGrid ref={listRef} style={listStyle} {...getCardColumns(minneData?.total_count || minneData.items.length, 3, 2)}>
          {minneData.items.map((response: any) => {
            const title = getMinneText(response?.title_i18n, currentLanguage) || response?.title;
            const excerpt = getMinneAnswer(response?.values?.[0], currentLanguage).join(" ");
            const name: string = response?.contributor?.display_name || "";
            const date = parseMinneDate(response?.created);
            return (
              <Card key={response?.uuid} onClick={() => setSelected(response)} onKeyDown={onCardKeyDown} role="button" tabIndex={0} aria-label={title || excerpt.slice(0, 80)}>
                {response?.image_dms_id && <FadeImage src={getCardImageUrl(response.image_dms_id)} alt="" loading="lazy"/>}
                <CardBody>
                  <Quote aria-hidden="true"/>
                  {title && <CardTitle>{title}</CardTitle>}
                  <Excerpt>{excerpt}</Excerpt>
                  <Byline>
                    <Avatar name={name} size={30}/>
                    <div>
                      {name}
                      {!isNaN(date) && <span>{formatDate(date)}</span>}
                    </div>
                  </Byline>
                </CardBody>
              </Card>
            );
          })}
        </CardGrid>
      )}
      <Pagination currentPage={currentPage} itemCount={minneData?.total_count || 0} pageSize={pageSize} setCurrentPage={setCurrentPage}/>
      {selected && <MinneModal uuid={selected.uuid} country={country} preview={selected} onClose={() => setSelected(null)}/>}
    </BlockContainer>
  );
}
